import Validator, { RequiredRule } from 'devextreme-react/validator';
import { DateBox } from 'devextreme-react/date-box';
import { useContext } from 'react';
import { useGetCompany } from '../../../../api/company';
import { Field, Panel } from '../../../../layouts';
import { AR, BusinessFlow, ROType } from '../../../../types/enums';
import { formatDateWithTimeframe, formatShortDate, TimeframeType } from '../../../../utils/date-utils';
import { RetailOrderContext } from '../context/RetailOrderContext';
import TextBox from 'devextreme-react/text-box';

export default function RetailOrderDatesPanel() {

	const { order, modifyOrder } = useContext(RetailOrderContext);
	const dealer = useGetCompany(order.details.dealerCompanyID);

	const isMeasureOrder = order.details.roTypeID === ROType.Measure || order.details.roTypeID === ROType.CabinetMeasureOrder;
	const isCabinetOrder = order.details.roTypeID === ROType.CabinetInstallOrder || order.details.roTypeID === ROType.CabinetMeasureOrder;
	const pendingChangeOrder = order.details.activeARs.includes(AR.DealerToConfirmChangeOrder);

	return (
		<Panel header={'Dates'} className={order.details.roTypeID === ROType.CabinetInstallOrder ? "field-label-70" : ""}
		>
			{
				order.details.isDeliveryOnly ?
					<>
						<Field label={'Scheduled Delivery Date'} >
							{formatDateWithTimeframe(order.details.scheduledDeliveryDate, TimeframeType.Long)}
						</Field>
						<Field label={'Actual Delivery Date'} >
							{formatShortDate(order.details.deliveryDate)}
						</Field>
					</>
					:
					<>
						{
							order.details.roTypeID !== ROType.CabinetInstallOrder &&
							<Field label={'Customer Contact Date'} >
								{formatShortDate(order.details.customerContactDate)}
							</Field>
						}
						{
							order.details.roTypeID !== ROType.CabinetInstallOrder &&
							<Field label={isMeasureOrder ? 'Ready to Measure Date' : 'Ready to Measure/Template Date'} >
								{formatShortDate(order.details.readyToMeasureOrTemplateDate)}
							</Field>
						}
						{
							order.details.roTypeID !== ROType.CabinetInstallOrder &&
							<Field label={isMeasureOrder ? 'Scheduled Measure Date' : 'Measure Date'}>
								{formatDateWithTimeframe(order.details.measureDate, TimeframeType.Short)}
							</Field>
						}
						{
							isMeasureOrder &&
							<Field label={'Actual Measure Date'}
								visible={dealer.data?.businessFlowID === BusinessFlow.Lowes}>
								{formatShortDate(order.details.actualMeasureDate)}
							</Field>
						}
						{
							!isMeasureOrder && !isCabinetOrder &&
							<>
								<Field label={'Template Date'} >
									{formatDateWithTimeframe(order.details.enterTemplateDate, TimeframeType.Short)}
								</Field>
								<Field label={'Scheduled Install Date'}>
									{formatDateWithTimeframe(order.details.scheduledInstallDate, TimeframeType.Long)}
								</Field>
								<Field label={'Actual Install Date'}>
									{formatShortDate(order.details.actualInstallDate)}
								</Field>
							</>
						}
						{
							!isMeasureOrder && isCabinetOrder &&
							<>
								<Field label={'Estimated Cabinet Delivery Date'}>
									{
										(order.details.activeARs.includes(AR.DealerToEnterEstimatedCabinetDeliveryDate) ||
											(order.details.resolvedARs.includes(AR.DealerToEnterEstimatedCabinetDeliveryDate) &&
												!order.details.resolvedARs.includes(AR.DealerToEnterCabinetPOReceivedDate))) ?
											<DateBox
												className='required'
												disabled={pendingChangeOrder}
												value={order.details.estimatedCabinetDeliveryDate}
												onValueChanged={(e) => {
													modifyOrder({
														...order.details,
														estimatedCabinetDeliveryDate: e.value
													})
												}}
											>
												<Validator>
													<RequiredRule message='Estimated Cabinet Delivery Date is required' />
												</Validator>
											</DateBox>
											:
											<>{formatShortDate(order.details.estimatedCabinetDeliveryDate)}</>
									}
								</Field>
								<Field label={'Cabinet Ship Date'}>
									{
										(order.details.activeARs.includes(AR.DealerToEnterEstimatedCabinetDeliveryDate) ||
											(order.details.resolvedARs.includes(AR.DealerToEnterEstimatedCabinetDeliveryDate) &&
												!order.details.resolvedARs.includes(AR.DealerToEnterCabinetPOReceivedDate))) ?
											<DateBox
												disabled={pendingChangeOrder}
												value={order.details.cabinetShipDate}
												onValueChanged={(e) => {
													modifyOrder({
														...order.details,
														cabinetShipDate: e.value
													})
												}}
											>
											</DateBox>
											:
											<>{formatShortDate(order.details.cabinetShipDate)}</>
									}
								</Field>
								<Field label={'Carrier Name'}>
									{
										(order.details.activeARs.includes(AR.DealerToEnterEstimatedCabinetDeliveryDate) ||
											(order.details.resolvedARs.includes(AR.DealerToEnterEstimatedCabinetDeliveryDate) &&
												!order.details.resolvedARs.includes(AR.DealerToEnterCabinetPOReceivedDate))) ?
											<TextBox
												maxLength={100}
												disabled={pendingChangeOrder}
												value={order.details.carrierName}
												onValueChanged={(e) => {
													modifyOrder({
														...order.details,
														carrierName: e.value
													})
												}}
											>
											</TextBox>
											:
											<>{order.details.carrierName}</>
									}
								</Field>
								<Field label={'Tracking Number'}>
									{
										(order.details.activeARs.includes(AR.DealerToEnterEstimatedCabinetDeliveryDate) ||
											(order.details.resolvedARs.includes(AR.DealerToEnterEstimatedCabinetDeliveryDate) &&
												!order.details.resolvedARs.includes(AR.DealerToEnterCabinetPOReceivedDate))) ?
											<TextBox
												maxLength={100}
												disabled={pendingChangeOrder}
												value={order.details.trackingNumber}
												onValueChanged={(e) => {
													modifyOrder({
														...order.details,
														trackingNumber: e.value
													})
												}}
											>
											</TextBox>
											:
											<>{order.details.trackingNumber}</>
									}
								</Field>
								<Field label={'Scheduled Install Start Date'}>
									{formatShortDate(order.details.scheduledInstallStartDate)}
								</Field>
								<Field label={'Scheduled Install Completion Date'}>
									{formatShortDate(order.details.scheduledInstallCompletionDate)}
								</Field>
								<Field label={'Cabinet PO Received Date'}>
									{
										order.details.activeARs.includes(AR.DealerToEnterCabinetPOReceivedDate) ?
											<DateBox
												className='required'
												disabled={pendingChangeOrder}
												value={order.details.cabinetPOReceivedDate}
												onValueChanged={(e) => {
													modifyOrder({
														...order.details,
														cabinetPOReceivedDate: e.value
													})
												}}
											>
												<Validator>
													{
														order.details.estimatedCabinetDeliveryDate === order.originalDetails.estimatedCabinetDeliveryDate &&
														<RequiredRule message='Cabinet PO Received Date is required' />
													}
												</Validator>
											</DateBox>
											:
											<>{formatShortDate(order.details.cabinetPOReceivedDate)}</>
									}
								</Field>
								<Field label={'Actual Install Start Date'}>
									{formatShortDate(order.details.actualInstallStartDate)}
								</Field>
								<Field label={'Lower Cabinets Plumb & Level Date'}>
									{formatShortDate(order.details.lowerCabinetsPlumbLevelDate)}
								</Field>
								<Field label={'Actual Install Completion Date'}>
									{formatShortDate(order.details.actualInstallCompletionDate)}
								</Field>
							</>
						}

					</>
			}
		</Panel>
	);
}
